import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Save, X } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // 必填验证
    if (!passwords.oldPassword || !String(passwords.oldPassword).trim()) {
      notifyError(t('profile.old_password_required'));
      return;
    }
    if (!passwords.newPassword || !String(passwords.newPassword).trim()) {
      notifyError(t('validation.password_required'));
      return;
    }
    if (!passwords.confirmPassword || !String(passwords.confirmPassword).trim()) {
      notifyError(t('profile.confirm_password_required'));
      return;
    }

    // 密码长度验证（和员工管理一致：>= 8）
    if (String(passwords.newPassword).length < 8) {
      notifyError(t('validation.password_min_length', { n: 8 }));
      return;
    }

    // 确认密码匹配验证
    if (passwords.newPassword !== passwords.confirmPassword) {
      notifyError(t('profile.passwords_not_match'));
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_password: passwords.oldPassword,
          new_password: passwords.newPassword,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notifySuccess(t('profile.password_change_success'));
        navigate('/profile');
      } else {
        notifyError(data.message || t('profile.password_change_failed'));
      }
    } catch (err) {
      notifyError(t('profile.network_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/profile')}
          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          title={t('common.back')}
        >
          <X size={20} />
        </button>
        <h1 className="text-2xl font-bold">{t('profile.change_password')}</h1>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <PasswordInput 
            label={t('profile.old_password')} 
            value={passwords.oldPassword} 
            onChange={e => handleChange('oldPassword', e.target.value)} 
          />
          <PasswordInput 
            label={t('profile.new_password')} 
            value={passwords.newPassword} 
            onChange={e => handleChange('newPassword', e.target.value)} 
          />
          <PasswordInput 
            label={t('profile.confirm_new_password')} 
            value={passwords.confirmPassword} 
            onChange={e => handleChange('confirmPassword', e.target.value)} 
          />

          <div className="flex justify-end gap-3 pt-4">
            <button 
              type="button" 
              onClick={() => navigate('/profile')} 
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm text-sm ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('common.processing')}
                </span>
              ) : (
                <><Save size={18} />{t('profile.update_password')}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
